import React, { useMemo, useState, useEffect } from 'react'
import { FormattedDate, FormattedTime, useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import messages from "./messages"
import UsageStatusChip from 'components/UsageStatusChip'
import UsageChainModal from './components/UsageChainModal'

import {
  Chip,
  Field
} from '@arnsbogroup/react-voicearchive'

import Icon from 'components/Icon'
import { getUsageChainTails, getFullChain } from 'utils/usageChainHelpers'

const OrderDetails = ({ order, fetchedEvents}) => {
  const { formatMessage } = useIntl()

  const [isPinging, setIsPinging] = useState(true);
  const [showUsageModal, setShowUsageModal] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);

  // Find USAGE chain tails
  const usageTasks = getUsageChainTails(order?.tasks || [])

  const handleUsageClick = (task) => {
    const fullChain = getFullChain(task, order?.tasks || [])
    setSelectedChain(fullChain)
    setShowUsageModal(true)
  }

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPinging(false);
    }, 5000); // 5 seconds
    return () => clearTimeout(timer);
  }, [fetchedEvents]);

  const importantLinkEventTypes = [
    {
      type: "AuditionScopeOverview",
      description: formatMessage(messages.auditionOverviewLink),
      link: () => `/orders/${order.uid}/overview`,
      condition: (event) => event?.meta && event?.meta?.hasBuyouts
    },
    {
      type: "Audition",
      description: formatMessage(messages.auditionLandingPageLink),
      link: (event) => {
        if(event?.meta && event?.meta?.link) {
          const fullUrl = event?.meta?.link
          const urlObject = new URL(fullUrl)
          const relativePath = urlObject.pathname
          return relativePath
        }
        return null
      },
      condition: (event) => event?.meta && event?.meta?.link
    },
    {
      type: "FeedbackRequest",
      description: formatMessage(messages.feedbackRequestLink),
      link: (event) => {
        if(event?.meta && event?.meta?.link) {
          const fullUrl = event?.meta?.link
          const urlObject = new URL(fullUrl)
          const relativePath = urlObject.pathname + urlObject.search
          return relativePath
        }
        return null
      },
      condition: (event) => event?.meta && event?.meta?.link
    }
  ]

  const importantLinkEvents = (fetchedEvents || [])
    .map((event) => {
        const linkType = importantLinkEventTypes.find(
            (importantLink) => importantLink.type === event.type
        );

        if (linkType && linkType.condition(event) && linkType.link(event)) {
            return {
                ...event,
                ...linkType,
            };
        }
        return null;
    })
    .filter(Boolean);

    const importantLinks = useMemo(() => {
      return (importantLinkEvents || [])
      .map((event, i) => {
        if (!event) {
          return null
        }
  
        return (
          <Link 
            to={event.link(event)}
            key={`item-${i}`}
            className="flex items-center my-1 text-primary cursor-pointer hover:text-primary-darker group"
          >
            <span className="relative inline-flex h-4 w-4">
              {isPinging && (
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              )}
              <Icon icon={["fas", "external-link"]} className="relative h-4 w-4" />
            </span>
            <span className="ml-2 text-xs">{event.description}</span>
          </Link>
        )
      })
    }, [importantLinkEvents, isPinging])

  const OrderStage = () => (
    <Chip size="xs" state={{
      'PENDING': 'info',
      'IN_PROGRESS': 'info',
      'READY': 'success',
      'CLOSED': 'error',
      'AMENDING': 'warning',
    }[order?.stage] ?? undefined}>
      <span className="capitalize">
        { order?.stage ? order?.stage.toLowerCase().replace('_', ' ') : '' }
      </span>
    </Chip>
  )

  return (
    <div className="w-full">
      <div className='m-2'>
        <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
          {order.name}
        </h2>

        <div className="flex flex-col md:flex-row w-full gap-x-4 sm:gap-x-6 gap-y-2">
          <div className='flex flex-wrap gap-x-4 sm:gap-x-6 gap-y-2'>
            <div className="text-sm text-gray-500"  title={formatMessage(messages.orderId)}>
              <Field label={formatMessage(messages.orderId)} labelClassName="text-xs">
                { order.uid }
              </Field>
            </div>

            <div className="text-sm text-gray-500" title={formatMessage(messages.deadline)}>
              <Field label={formatMessage(messages.deadline)} labelClassName="text-xs">
                  <FormattedDate value={new Date(+order.deadline * 1000)} /> <FormattedTime value={new Date(+order.deadline * 1000)} />
              </Field>
            </div>
            {order?.customerReference &&
              <div className="text-sm text-gray-500" title={formatMessage(messages.po)}>
                <Field label={formatMessage(messages.po)} labelClassName="text-xs">
                  { order.customerReference }
                </Field>
              </div>
            }

            <div className="text-sm text-gray-500" title={formatMessage(messages.status)}>
              <Field label={formatMessage(messages.status)} labelClassName="text-xs">
                { OrderStage() }
              </Field>
            </div>
            
            <div className="text-sm text-gray-500" title={formatMessage(messages.usageRights)}>
              <Field label={formatMessage(messages.usageRights)} labelClassName="text-xs">
                {!(!!usageTasks.length) &&
                  <div className='flex gap-1 flex-wrap'>
                    {!!order?.orderusage_name &&
                        <Chip>{ order?.orderusage_name }</Chip>
                    }
                    {Array.isArray(order?.medias) && !!order?.medias.length && order?.medias.map(({name}) =>
                      <Chip key={name}>{name}</Chip>
                    )}
                  </div>
                }
                {usageTasks.length > 0 && (
                  <div className='space-y-2'>
                    {usageTasks.map((task) => {
                      const airingTimeText = task?.usage && task?.usage[0]
                        ? task?.usage[0]
                        : formatMessage(messages.usageNotSet)
                      const mediaText = task?.medias && task.medias.length > 0
                        ? task.medias.map(m => `${m.name} (${m.spot_count})`).join(', ')
                        : formatMessage(messages.usageNotSet)
                      const territoryMarketText = task?.territoryMarkets && task.territoryMarkets.length > 0
                        ? task.territoryMarkets.map(tm => tm.name).join(', ')
                        : formatMessage(messages.usageNotSet)

                      return (
                        <div
                          key={task.uid}
                          onClick={() => handleUsageClick(task)}
                          className='cursor-pointer'
                          title={formatMessage(messages.usageChipTitle)}
                        >
                          <UsageStatusChip
                            task={task}
                            includeFullUsage={true}
                            airingTimeText={airingTimeText}
                            mediaText={mediaText}
                            territoryMarketText={territoryMarketText}
                          />
                        </div>
                      )
                    })}
                  </div>
                )}
              </Field>
            </div>
          </div>
          {importantLinks && !!importantLinks.length &&
            <div>
              <Field label={formatMessage(messages.importantLinks)} labelClassName="text-xs">
                {importantLinks}
              </Field>
            </div>
          }
        </div>
      </div>

      <UsageChainModal
        show={showUsageModal}
        onClose={() => setShowUsageModal(false)}
        chain={selectedChain}
      />
    </div>
  )
}

export default OrderDetails